import React from 'react'
import {
	Box,
	Typography,
	Paper,
	Divider,
	Stack,
	alpha,
	Chip,
} from '@mui/material'
import { CheckCircle, Warning, CheckCircleOutline } from '@mui/icons-material'
import { ScaffoldUpdateRecord } from '../../../types/Scaffold/ScaffoldRequest'
import {
	formatLocaleTime,
	formatTimelineDate,
} from '../../../services/CommonServices/DataFormatters'

interface UpdateHistoryProps {
	records: ScaffoldUpdateRecord[]
	projectTimeZone: string
}

export function UpdateHistory({ records, projectTimeZone }: UpdateHistoryProps) {
	const sortedRecords = [...records].sort((a, b) => {
		return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
	})

	return (
		<Paper
			variant='outlined'
			sx={(theme) => ({
				p: 2.5,
				borderRadius: 2,
				background: `linear-gradient(to bottom, ${alpha(
					theme.palette.primary.main,
					0.02,
				)}, ${alpha(theme.palette.background.paper, 1)})`,
				border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
				boxShadow: theme.shadows[1],
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
			})}
		>
			<Box>
				<Typography
					variant='subtitle1'
					sx={{
						fontWeight: 600,
						color: 'primary.main',
						mb: 0.25,
					}}
				>
					Update History
				</Typography>
				<Typography variant='caption' color='text.secondary'>
					{records.length} record{records.length !== 1 ? 's' : ''}
				</Typography>
			</Box>
			<Divider sx={{ my: 1.5 }} />
			<Box
				sx={{
					flex: 1,
					overflowY: 'auto',
					maxHeight: '500px',
					pr: 0.5,
					position: 'relative',
				}}
			>
				{records.length === 0 ? (
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							py: 4,
							color: 'text.secondary',
						}}
					>
						<Typography variant='body2'>No update records found</Typography>
					</Box>
				) : (
					<Box sx={{ position: 'relative', pl: 3 }}>
						{/* Vertical Timeline Line */}
						<Box
							sx={(theme) => ({
								position: 'absolute',
								left: '12px',
								top: '20px',
								bottom: '20px',
								width: '2px',
								backgroundColor: alpha(theme.palette.primary.main, 0.3),
							})}
						/>
						<Stack spacing={0}>
							{sortedRecords.map((record, index) => {
								const statusColor =
									record.status === 0
										? 'success'
										: record.status === 1
										? 'warning'
										: 'error'
								const isLast = index === sortedRecords.length - 1
								return (
									<Box
										key={record.id}
										sx={{
											display: 'flex',
											position: 'relative',
											pb: isLast ? 0 : 3,
										}}
									>
										{/* Timeline Circle */}
										<Box
											sx={(theme) => ({
												position: 'absolute',
												left: '-24px',
												top: '4px',
												width: '24px',
												height: '24px',
												borderRadius: '50%',
												backgroundColor: theme.palette[statusColor].main,
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												border: `2px solid ${theme.palette.background.paper}`,
												zIndex: 1,
											})}
										>
											<CheckCircle
												sx={{
													fontSize: '16px',
													color: 'white',
												}}
											/>
										</Box>
										{/* Content */}
										<Box sx={{ flex: 1, pl: 2 }}>
											<Typography
												variant='body2'
												sx={{
													fontWeight: 600,
													color: 'text.primary',
													mb: 1,
													fontSize: '0.875rem',
												}}
											>
												{formatTimelineDate(
													projectTimeZone || 'UTC',
													record.updatedAt,
												)}{' '}
												{formatLocaleTime(projectTimeZone || 'UTC', record.updatedAt)}
											</Typography>
											
											{/* Comment/Note */}
											{record.note && (
												<Box sx={{ mb: 1.5 }}>
													<Typography
														variant='body2'
														sx={{
															fontWeight: 500,
															color: 'text.primary',
															mb: 0.5,
															fontSize: '0.8125rem',
														}}
													>
														Comment:
													</Typography>
													<Typography
														variant='body2'
														sx={{
															color: 'text.secondary',
															fontSize: '0.875rem',
															whiteSpace: 'pre-wrap',
														}}
													>
														{record.note}
													</Typography>
												</Box>
											)}
											
											{/* Fall Protection Required */}
											<Box sx={{ mb: 1.5 }}>
												<Typography
													variant='body2'
													sx={{
														fontWeight: 500,
														color: 'text.primary',
														mb: 0.5,
														fontSize: '0.8125rem',
													}}
												>
													Fall Protection Required:
												</Typography>
												<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
													{record.fallProtectionRequired ? (
														<>
															<Warning sx={{ fontSize: '16px', color: 'error.main' }} />
															<Typography
																variant='body2'
																sx={{
																	color: 'error.main',
																	fontWeight: 500,
																	fontSize: '0.875rem',
																}}
															>
																Yes
															</Typography>
														</>
													) : (
														<>
															<CheckCircleOutline sx={{ fontSize: '16px', color: 'info.main' }} />
															<Typography
																variant='body2'
																sx={{
																	color: 'info.main',
																	fontWeight: 500,
																	fontSize: '0.875rem',
																}}
															>
																No
															</Typography>
														</>
													)}
												</Box>
											</Box>
											
											{/* Incomplete Items */}
											{record.incompleteItems && record.incompleteItems.length > 0 && (
												<Box sx={{ mb: 1.5 }}>
													<Typography
														variant='body2'
														sx={{
															fontWeight: 500,
															color: 'text.primary',
															mb: 0.75,
															fontSize: '0.8125rem',
														}}
													>
														Incomplete Items:
													</Typography>
													<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
														{record.incompleteItems.map((item, itemIndex) => (
															<Chip
																key={itemIndex}
																label={item}
																size='small'
																sx={{
																	fontSize: '0.75rem',
																	height: '24px',
																	backgroundColor: (theme) =>
																		alpha(theme.palette.warning.main, 0.1),
																	color: 'warning.dark',
																	border: (theme) =>
																		`1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
																}}
															/>
														))}
													</Box>
												</Box>
											)}
											
											<Typography
												variant='body2'
												sx={{
													color: 'text.secondary',
													fontSize: '0.8125rem',
													mt: 0.5,
												}}
											>
												Updated by {record.updatedBy}
											</Typography>
										</Box>
									</Box>
								)
							})}
						</Stack>
					</Box>
				)}
			</Box>
		</Paper>
	)
}

